/**
 * The daily ritual — the sequence Home runs before it will show you anything else.
 *
 * This file holds only the parts that are decidable without a screen: the
 * greeting, the sentence the app says back to you, and the handful of times it
 * offers when it asks "when will you do it?". Keeping them here means they are
 * testable, and it means the readback vocabulary has exactly one home — the
 * check-in card and the ritual must never drift into saying today differently.
 */

/**
 * How today gets said back to the person, in a sentence.
 *
 * MOOD_LABELS / ENERGY_LABELS in utils/mood are the vocabulary of the control
 * you tap, which is the right register for a button and the wrong one for an
 * acknowledgement. "Mood: Low. Energy: Low." is a triage readout; "A low day,
 * running quiet." is somebody being heard. Same two numbers.
 */
export const MOOD_PHRASE = [
  "A heavy day",
  "A low day",
  "An even day",
  "A good day",
  "A bright day",
];

export const ENERGY_PHRASE = [
  "running on empty",
  "running quiet",
  "steady with it",
  "with energy to spare",
  "wired with it",
];

/** The small receipt under the sentence — sentence case, never a shout. */
export const MOOD_WORD = ["awful", "low", "OK", "good", "great"];
export const ENERGY_WORD = ["drained", "low", "steady", "fresh", "wired"];

export function readbackLine(mood, energy) {
  if (!MOOD_PHRASE[mood - 1] || !ENERGY_PHRASE[energy - 1]) return "";
  return `${MOOD_PHRASE[mood - 1]}, ${ENERGY_PHRASE[energy - 1]}.`;
}

/**
 * The greeting the ritual opens on.
 *
 * The small hours get their own greeting and it is not "Good morning". This
 * product's population is defined in part by not sleeping; greeting somebody
 * good morning at 2am, when they are awake because they cannot sleep, is the
 * app failing to notice the single most obvious thing about the moment.
 */
export function ritualGreeting(now = new Date()) {
  const h = now.getHours();
  if (h < SMALL_HOURS_END) return "You're up late";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/** Before this hour, it is still last night as far as a person is concerned. */
const SMALL_HOURS_END = 5;

function hhmm(d) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * The times the ritual offers, given the time of day.
 *
 * Two rules, both of which exist because a commitment that is already in the
 * past is worse than no commitment at all:
 *
 *   1. Every option returned is later than — or equal to — `now`.
 *   2. Nothing ever crosses midnight. A commitment is keyed to a date, so an
 *      "in an hour" at 23:40 would land at 00:40 tomorrow and read as several
 *      hours overdue the moment it was made.
 *
 * "Right now" is always first, because on the days this product is for, the
 * honest answer to "when?" is usually "if not now, not at all."
 */
export function ritualTimeChoices(now = new Date()) {
  const h = now.getHours();

  /**
   * Between midnight and five, the app stops offering the next hour.
   *
   * It used to hand a patient at 00:15 "In an hour — 1:00 AM" alongside "This
   * evening — 6:30 PM" on the same screen, under a "Good morning" — which is
   * both incoherent and, when the activation on offer is "walk once around the
   * block", the app proposing that somebody who is awake at midnight because
   * they are unwell go outside alone at 1am. A patient may still choose to do
   * something now; the product will not be the one to suggest 3am.
   */
  if (h < SMALL_HOURS_END) {
    return [
      { key: "morning", label: "Later this morning", time: "09:00" },
      { key: "afternoon", label: "This afternoon", time: "14:00" },
      { key: "evening", label: "This evening", time: "18:30" },
    ];
  }

  const out = [{ key: "now", label: "Right now", time: hhmm(now) }];

  if (h < 22) {
    const later = new Date(now);
    later.setHours(h + 1, 0, 0, 0);
    out.push({ key: "hour", label: "In an hour", time: hhmm(later) });
  }

  if (h < 16) out.push({ key: "evening", label: "This evening", time: "18:30" });
  else if (h < 20) out.push({ key: "tonight", label: "Tonight", time: "20:30" });
  else if (h < 23) out.push({ key: "bed", label: "Before bed", time: "23:00" });

  return out;
}
